import {
  DETAIL_ANIMAL_SEARCH_TYPES,
  SEARCH_TYPE,
} from '@/constants/searchType';
import { SESSION_STATUS } from '@/constants/auth';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSession } from 'next-auth/react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useDatadogTracker } from '@/hooks/publicSearch/datadog/useDatadogTracker';
import { EventType } from '@/lib/types';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tabs,
  Tooltip,
  Typography,
} from '@/primitives';
import MyHerdLinkingInfoDialog from '@/components/publicSearch/MyHerdLinkingInfoDialog';
import BasicSearchPercentileInfoDialog from './BasicSearchPercentileInfoDialog';
import BreedplanInterpretationInfoTrigger from '@/components/publicSearch/animal/ebv/BreedplanInterpretationInfoTrigger';
import useFilterStore from '@/publicSearch/store/useFilterStore';
import useGridStore from '@/publicSearch/store/useGridStore';
import { useSearch } from '@/hooks/publicSearch/useSearch';
import { useSearchPrecheck } from '@/hooks/publicSearch/useSearchPrecheck';
import useSearchStore from '@/publicSearch/store/useSearchStore';
import { useSearchType } from '@/hooks/publicSearch/useSearchType';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { useIsEbvEnabled } from '@/hooks/publicSearch/useIsEbvEnabled';
import { useEbvDisplayOptions } from '@/hooks/publicSearch/useEbvDisplayOptions';
import { useMemberAssociation } from '@/publicSearch/graphql/memberAssociation/hooks';
import { abortAllPendingGraphQL } from '@/publicSearch/apolloClient';
import { usePreviewCount } from '@/hooks/publicSearch/usePreviewCount';
import BasicSearchCondition from './BasicSearchCondition';
import { upsertHeroSearchTermInFilters } from '../quickSearchFilterBuilder';
import BasicSearchEbvCondition from './BasicSearchEbvCondition';
import BasicSearchLookupCondition from './BasicSearchLookupCondition';
import { type BasicSearchField } from '../constants';
import DynamicFilterSection from './DynamicFilterSection';
import {
  FilterInput,
  LogicalOperator,
  FieldOption,
  OperatorOption,
} from '../../constants';
import { deepCopy } from '@/utils/common';
import {
  CATEGORY_COLOR_MAP,
  getEbvConfigGroupedByCategory,
  ebvGroupingOptionsFromDisplay,
  parseEbvConfig,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import {
  EBV_TAB_VALUE,
  buildBasicSearchTabDefs,
} from './basicSearchDialogUtils';
import { MyHerdToggle, OnSaleToggle } from './BasicSearchDialogToggles';
import { useBasicSearchFields } from './useBasicSearchFields';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function BasicSearchDialog({ open, onClose }: Props) {
  const searchType = useSearchType();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { status } = useSession();
  const isAuthenticated = status === SESSION_STATUS.AUTHENTICATED;
  const signInCallbackUrl = (() => {
    const q = searchParams?.toString();
    return q ? `${pathname}?${q}` : pathname;
  })();
  const {
    commonFilterOperator,
    filters,
    clearFilter,
    clearFilterGroups,
    setFilters,
    updateCommonFilterOperator,
  } = useFilterStore(searchType);
  const orgId = usePublicSearchContext((context) => context.society.orgId);
  const society = usePublicSearchContext((context) => context.society);
  const {
    myAnimals,
    animalsOnSaleOnly,
    setMyAnimals,
    setAnimalsOnSaleOnly,
    paginationModel,
    setPaginationModel,
  } = useGridStore();
  const { data: memberAssocData } = useMemberAssociation(!isAuthenticated);
  const hasMemberAssociations =
    (memberAssocData?.memberAssociation?.length ?? 0) > 0;
  const showMyAnimalsSection = searchType === SEARCH_TYPE.ANIMAL;

  const { basicSearchInput, setBasicSearch, setOpenResult } = useSearchStore();

  const ebvConfig = useMemo(
    () => parseEbvConfig(society.settings),
    [society.settings],
  );
  const isEbvEnabled = useIsEbvEnabled();
  const {
    ebvLabel,
    showPercentileSearch,
    showBreedplanHelp,
    useColourSlotGrouping,
  } = useEbvDisplayOptions();
  const ebvGroupingOptions = useMemo(
    () => ebvGroupingOptionsFromDisplay({ useColourSlotGrouping }),
    [useColourSlotGrouping],
  );
  const dialogTabs = useMemo(() => {
    const tabs = buildBasicSearchTabDefs(ebvLabel);
    return isEbvEnabled ? tabs : tabs.filter((t) => t.value !== EBV_TAB_VALUE);
  }, [isEbvEnabled, ebvLabel]);
  const effectiveEbvConfig = useMemo(
    () => (isEbvEnabled ? ebvConfig : []),
    [isEbvEnabled, ebvConfig],
  );

  const [tabValue, setTabValue] = useState<string>(SEARCH_TYPE.ANIMAL);
  const [localFilters, setLocalFilters] = useState<FilterInput[]>([]);
  const localFiltersRef = useRef<FilterInput[]>(localFilters);
  const [dynamicFilters, setDynamicFilters] = useState<FilterInput[]>([]);
  const dynamicFiltersRef = useRef<FilterInput[]>(dynamicFilters);

  useEffect(() => {
    localFiltersRef.current = localFilters;
  }, [localFilters]);

  useEffect(() => {
    dynamicFiltersRef.current = dynamicFilters;
  }, [dynamicFilters]);
  const [dynamicInitialFilters, setDynamicInitialFilters] = useState<
    FilterInput[]
  >([]);
  const [dynamicResetKey, setDynamicResetKey] = useState<number>(0);
  const [myHerdLinkingInfoOpen, setMyHerdLinkingInfoOpen] =
    useState<boolean>(false);
  const [percentileInfoOpen, setPercentileInfoOpen] = useState<boolean>(false);

  const isDetailAnimalsSearch = DETAIL_ANIMAL_SEARCH_TYPES.includes(searchType);

  useEffect(() => {
    if (!isEbvEnabled && tabValue === EBV_TAB_VALUE) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- corrects the active tab back to Animal when EBVs become disabled while the EBV tab is selected; tabValue is also set by handleTabChange, so it is user-controlled and not purely derived
      setTabValue(SEARCH_TYPE.ANIMAL);
    }
  }, [isEbvEnabled, tabValue]);

  const { fields, fieldValues, allFixedFieldValues, dynamicFieldOptions } =
    useBasicSearchFields({
      searchType,
      isDetailAnimalsSearch,
      tabValue,
      societySettings: society.settings,
      effectiveEbvConfig,
      ebvLabel,
      showPercentileSearch,
    });

  const allFilters = useMemo(
    () => [...localFilters, ...dynamicFilters],
    [localFilters, dynamicFilters],
  );

  const query = useSearch();
  const searchCount = query.data?.totalCount;
  const searchActive = query.searchActive;

  const hasIncompleteFilters = useMemo(
    () =>
      allFilters.some(
        (f) =>
          !f.field.startsWith('__lookup__') &&
          f.operator &&
          f.operator !== OperatorOption.isNotEmpty.value &&
          f.operator !== 'LOOKUP' &&
          (!f.value || !String(f.value).trim()),
      ),
    [allFilters],
  );

  const previewCount = usePreviewCount({
    filters: allFilters,
    searchType,
    commonFilterOperator: LogicalOperator.and,
    enabled: open && !hasIncompleteFilters && searchActive,
  });

  const { hideError } = useSearchPrecheck(
    searchActive && typeof searchCount === 'undefined',
  );

  const searchStartTimeRef = useRef<number | null>(null);
  const { trackEvent } = useDatadogTracker();
  const lastSyncedBasicSearchInput = useRef<string | null>(null);
  const prevOpenRef = useRef<boolean>(false);

  // When dialog opens, copy store filters into local state so we don't trigger grid/query until Show results
  useEffect(() => {
    if (!open) {
      prevOpenRef.current = false;
      return;
    }
    if (!prevOpenRef.current) {
      const storeFilters = Array.isArray(filters) ? filters : [];
      const fixed = storeFilters.filter(
        (f) =>
          allFixedFieldValues.has(f.field) || f.field.startsWith('__lookup__'),
      );
      const dynamic = storeFilters.filter(
        (f) =>
          !allFixedFieldValues.has(f.field) &&
          !f.field.startsWith('__lookup__'),
      );
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time copy of store filters into local working state when the dialog opens (guarded by prevOpenRef); these states are also written by the search/reset handlers, so they are not purely derived
      setLocalFilters(fixed);

      setDynamicFilters(dynamic);

      setDynamicInitialFilters(dynamic);
      prevOpenRef.current = true;
    }
  }, [open, filters, allFixedFieldValues]);

  const heroCompositeField = useMemo(() => {
    if (searchType === SEARCH_TYPE.ANIMAL || isDetailAnimalsSearch) {
      return FieldOption.searchTerm.value;
    }
    if (searchType === SEARCH_TYPE.MEMBERS) {
      return FieldOption.member.value;
    }
    if (searchType === SEARCH_TYPE.SALE_CATALOGUES) {
      return FieldOption.saleName.value;
    }
    return null;
  }, [searchType, isDetailAnimalsSearch]);

  // Sync hero term into the basic-search composite field (LD default + wildcard chips).
  useEffect(() => {
    if (open) return;
    if (!heroCompositeField) {
      if (!open) {
        lastSyncedBasicSearchInput.current = null;
      }
      prevOpenRef.current = open;
      return;
    }

    const searchTermTrimmed = basicSearchInput?.trim() || '';
    const dialogJustOpened = !prevOpenRef.current && open;
    prevOpenRef.current = open;

    // When dialog just opened with empty hero box, don't overwrite filters (e.g. Animal Equals from quick search in dialog)
    if (dialogJustOpened && !searchTermTrimmed) {
      return;
    }

    // Skip if we've already synced this value
    if (lastSyncedBasicSearchInput.current === searchTermTrimmed) {
      return;
    }

    lastSyncedBasicSearchInput.current = searchTermTrimmed;

    const currentFilters = Array.isArray(filters) ? filters : [];
    const nextFilters = upsertHeroSearchTermInFilters(
      currentFilters,
      searchTermTrimmed,
      heroCompositeField,
    );

    if (nextFilters !== currentFilters) {
      setFilters(nextFilters);
    }
  }, [
    open,
    basicSearchInput,
    searchType,
    filters,
    setFilters,
    isDetailAnimalsSearch,
    heroCompositeField,
  ]);

  const searchButtonLabel = useMemo(() => {
    if (searchActive && (query.loading || previewCount.loading)) {
      return (
        <>
          <CircularProgress size={12} sx={{ color: 'inherit', mr: 0.75 }} />
          Updating results
        </>
      );
    }
    if (typeof previewCount.count === 'number') {
      return `Show ${previewCount.count.toLocaleString()} results`;
    }
    return 'Show results';
  }, [searchActive, query.loading, previewCount.loading, previewCount.count]);

  const getUniqueFilters = useCallback(() => {
    const source = localFilters;
    const standardFilters = source.filter(({ field }) =>
      fieldValues.includes(field),
    );
    const uniqueFilters = standardFilters.reduce((acc: FilterInput[], cur) => {
      const exists = acc.find((a) => a.field === cur.field);
      if (!exists) acc.push(cur);
      return acc;
    }, []);
    const lookupFilters = source.filter(
      ({ field }) =>
        field.startsWith('__lookup__') || !fieldValues.includes(field),
    );
    return deepCopy([...uniqueFilters, ...lookupFilters]);
  }, [localFilters, fieldValues]);

  const handleDynamicFiltersChange = useCallback((filters: FilterInput[]) => {
    setDynamicFilters(filters);
  }, []);

  function handleSearch() {
    hideError();

    let currentFixed = localFiltersRef.current;

    if (commonFilterOperator === LogicalOperator.or) {
      currentFixed = getUniqueFilters();
      setLocalFilters(currentFixed);
      updateCommonFilterOperator(LogicalOperator.and);
    }

    const combined = [...currentFixed, ...dynamicFiltersRef.current];
    searchStartTimeRef.current = performance.now();
    // Keep __lookup__ marker rows — buildMemberFilterGroups / buildAnimalFilterGroups
    // expand them via society settings at query time (same pipeline as preview count).
    // Stripping them here left lookup-only basic searches with an empty store payload.
    clearFilterGroups();
    setFilters(combined);
    onClose();
    setOpenResult(true);
  }

  function handleResetFilters() {
    abortAllPendingGraphQL();
    clearFilter();
    // Keep the results grid open — an empty search renders the default list.
    setOpenResult(true);
    hideError();
    onClose();
    setBasicSearch('');
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    setDynamicFilters([]);
    setDynamicInitialFilters([]);
    setDynamicResetKey((k) => k + 1);

    if (searchType === SEARCH_TYPE.ANIMAL || searchType === SEARCH_TYPE.MEMBERS)
      return;
    handleSearch();
  }

  function handleTabChange(value: string) {
    setTabValue(value);
  }

  useEffect(() => {
    if (
      searchStartTimeRef.current !== null &&
      !query.loading &&
      typeof searchCount === 'number' &&
      orgId
    ) {
      const durationMs = performance.now() - searchStartTimeRef.current;

      trackEvent?.(
        'SearchExecuted',
        {
          searchType,
          searchTerm: '',
          filters,
          filterFieldsUsed: filters.map((f) => f.field).join(','),
          logicalOperator: commonFilterOperator,
          filterCount: filters.length,
          resultCount: searchCount,
          triggeredFrom: 'advanced',
          orgId,
          durationMs: Math.round(durationMs),
        },
        EventType.FORM_SUBMIT,
      );

      searchStartTimeRef.current = null;
    }
  }, [
    commonFilterOperator,
    filters,
    orgId,
    query.loading,
    searchCount,
    searchType,
    trackEvent,
  ]);

  const myHerdToggle = (
    <MyHerdToggle
      isAuthenticated={isAuthenticated}
      hasMemberAssociations={hasMemberAssociations}
      myAnimals={myAnimals}
      signInCallbackUrl={signInCallbackUrl}
      paginationPageSize={paginationModel.pageSize}
      setMyAnimals={setMyAnimals}
      setPaginationModel={setPaginationModel}
      setMyHerdLinkingInfoOpen={setMyHerdLinkingInfoOpen}
    />
  );

  const onSaleToggle = (
    <OnSaleToggle
      animalsOnSaleOnly={animalsOnSaleOnly}
      paginationPageSize={paginationModel.pageSize}
      setAnimalsOnSaleOnly={setAnimalsOnSaleOnly}
      setPaginationModel={setPaginationModel}
    />
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        disableEnforceFocus
        slotProps={{
          paper: {
            sx: {
              m: { xs: '20px', sm: 0 },
              maxHeight: {
                xs: 'calc(100% - 40px)',
                sm: 'calc(100% - 64px)',
              },
              width: { xs: 'calc(100% - 40px)', sm: 'calc(100% - 64px)' },
            },
          },
        }}
      >
        <DialogTitle
          component="div"
          sx={{ p: { xs: '24px 16px 16px 16px', sm: '24px 32px 24px 32px' } }}
        >
          <Typography sx={{ mb: '16px' }} variant="headlinesmall">
            Basic Search
          </Typography>
          <Typography sx={{ color: 'grey' }} variant="bodymedium">
            Refine your search by selecting criteria below.
          </Typography>
        </DialogTitle>
        <IconButton
          aria-label="close"
          sx={(theme) => ({
            position: 'absolute',
            right: { xs: '4px', sm: '20px' },
            top: '20px',
            color: theme.palette.text.primary,
          })}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
        {(searchType === SEARCH_TYPE.ANIMAL || isDetailAnimalsSearch) &&
          dialogTabs.length > 1 && (
            <Box
              sx={{
                px: { xs: '16px', sm: '24px' },
                pb: '16px',
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: { xs: 1, sm: 2 },
                minHeight: { xs: '52px', sm: '64px' },
                overflow: 'hidden',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                <Tabs
                  tabButtonProps={{
                    sx: {
                      px: { xs: '12px', sm: '24px' },
                      py: { xs: '18px', sm: '24px' },
                    },
                  }}
                  tabs={dialogTabs}
                  value={tabValue}
                  onClick={(value) => handleTabChange(value)}
                />
              </Box>
            </Box>
          )}
        <DialogContent
          dividers
          sx={{
            px: { xs: '16px', sm: '24px' },
            py: '16px !important',
            minHeight: '400px',
            maxHeight: '500px',
            overflowY: 'auto',
          }}
        >
          {(() => {
            if (
              tabValue === SEARCH_TYPE.ANIMAL &&
              (searchType === SEARCH_TYPE.ANIMAL || isDetailAnimalsSearch)
            ) {
              // Animal Details tab - show standard fields + society setting fields
              return (
                <>
                  {showMyAnimalsSection && (
                    <Box
                      sx={{
                        mb: 1.5,
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: { xs: 2, sm: 3 },
                      }}
                    >
                      {myHerdToggle}
                      {onSaleToggle}
                    </Box>
                  )}
                  {fields.map((field) => {
                    const f = field as BasicSearchField;
                    if (f.lookups?.length) {
                      return (
                        <BasicSearchLookupCondition
                          key={f.value}
                          field={f}
                          filtersOverride={localFilters}
                          setFiltersOverride={setLocalFilters}
                        />
                      );
                    }
                    return (
                      <BasicSearchCondition
                        key={field.value}
                        field={field}
                        getUniqueFilters={getUniqueFilters}
                        filtersOverride={localFilters}
                        setFiltersOverride={setLocalFilters}
                      />
                    );
                  })}
                </>
              );
            }
            if (
              tabValue === EBV_TAB_VALUE &&
              (searchType === SEARCH_TYPE.ANIMAL || isDetailAnimalsSearch)
            ) {
              // EBVs and Indexes tab - show EBV filters grouped by category
              return (
                <>
                  {(showMyAnimalsSection ||
                    showPercentileSearch ||
                    showBreedplanHelp) && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: showMyAnimalsSection
                          ? 'space-between'
                          : 'flex-end',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 1,
                        mb: 1.5,
                      }}
                    >
                      {showMyAnimalsSection && (
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: { xs: 2, sm: 3 },
                          }}
                        >
                          {myHerdToggle}
                          {onSaleToggle}
                        </Box>
                      )}
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          gap: 1.5,
                          ml: showMyAnimalsSection ? undefined : 'auto',
                        }}
                      >
                        {showBreedplanHelp && (
                          <BreedplanInterpretationInfoTrigger
                            dataDdActionName="Basic Search: BREEDPLAN Interpretation Info"
                            label="BREEDPLAN interpretation"
                          />
                        )}
                        {showPercentileSearch && (
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Tooltip
                              title="About BREEDPLAN percentile filtering"
                              placement="top"
                            >
                              <IconButton
                                size="small"
                                aria-label="About BREEDPLAN percentile filtering. Opens explanation dialog."
                                data-dd-action-name="Basic Search: Percentile info"
                                onClick={() => setPercentileInfoOpen(true)}
                                sx={{
                                  p: 0.25,
                                  color: 'text.secondary',
                                }}
                              >
                                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Typography
                              component="span"
                              variant="bodysmall"
                              sx={{
                                color: 'text.secondary',
                                fontWeight: 500,
                                userSelect: 'none',
                              }}
                            >
                              BREEDPLAN percentile filtering
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                  {getEbvConfigGroupedByCategory(
                    effectiveEbvConfig,
                    ebvGroupingOptions,
                  ).map(({ category, items }, index) => (
                    <Box key={category} sx={{ mt: index === 0 ? 0 : 1.25 }}>
                      <Typography
                        component="div"
                        variant="labelmedium"
                        sx={{
                          color: 'text.primary',
                          fontWeight: 600,
                          mb: 0.5,
                          // Match BasicSearchEbvCondition trait label + Min EBV row (`pl: '4px'`).
                          pl: '4px',
                          fontSize: '14px',
                          lineHeight: 1.2,
                        }}
                      >
                        {category}
                      </Typography>
                      {items.map((item) => {
                        const fieldNumber = item.field.replace('ebv', '');
                        const displayLabel = item.label.replace(/\^/g, ' ');
                        const ebvField = `ebv${fieldNumber}`;
                        const ebvAccField = `ebv${fieldNumber}Acc`;
                        return (
                          <BasicSearchEbvCondition
                            key={item.field}
                            ebvField={ebvField}
                            ebvAccField={ebvAccField}
                            label={displayLabel}
                            isIndexEbv={category === 'Indexes'}
                            ebvFormat={item.format}
                            ebvLeftLabel={item.leftLabel}
                            ebvRightLabel={item.rightLabel}
                            percentileTrackColor={
                              CATEGORY_COLOR_MAP[category] ??
                              CATEGORY_COLOR_MAP.Other
                            }
                            getUniqueFilters={getUniqueFilters}
                            filtersOverride={localFilters}
                            setFiltersOverride={setLocalFilters}
                          />
                        );
                      })}
                    </Box>
                  ))}
                </>
              );
            }
            // Members or other search types
            return (
              <>
                {fields.map((field) => {
                  const f = field as BasicSearchField;
                  if (f.lookups?.length) {
                    return (
                      <BasicSearchLookupCondition
                        key={f.value}
                        field={f}
                        filtersOverride={localFilters}
                        setFiltersOverride={setLocalFilters}
                      />
                    );
                  }
                  return (
                    <BasicSearchCondition
                      key={field.value}
                      field={field}
                      getUniqueFilters={getUniqueFilters}
                      filtersOverride={localFilters}
                      setFiltersOverride={setLocalFilters}
                    />
                  );
                })}
              </>
            );
          })()}
          {((searchType !== SEARCH_TYPE.ANIMAL && !isDetailAnimalsSearch) ||
            tabValue !== EBV_TAB_VALUE) && (
            <DynamicFilterSection
              fieldOptions={dynamicFieldOptions}
              onChange={handleDynamicFiltersChange}
              initialFilters={dynamicInitialFilters}
              resetKey={dynamicResetKey}
            />
          )}
          {hasIncompleteFilters && (
            <Typography
              variant="labelmedium"
              sx={{
                color: 'warning.main',
              }}
            >
              Some filters are incomplete. Please complete or remove them to see
              the result count.
            </Typography>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            flexDirection: 'row',
            gap: '16px',
            justifyContent: 'end',
          }}
        >
          <Button color="info" variant="text" onClick={handleResetFilters}>
            Reset filters
          </Button>
          {!hasIncompleteFilters && (
            <Button
              color="info"
              disabled={searchActive && (query.loading || previewCount.loading)}
              variant="contained"
              onClick={handleSearch}
            >
              {searchButtonLabel}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <MyHerdLinkingInfoDialog
        open={myHerdLinkingInfoOpen}
        onClose={() => setMyHerdLinkingInfoOpen(false)}
      />
      {showPercentileSearch && (
        <BasicSearchPercentileInfoDialog
          open={percentileInfoOpen}
          onClose={() => setPercentileInfoOpen(false)}
        />
      )}
    </>
  );
}
